/**
 * v2 /v2/alarms — cross-chassis alarms list.
 *
 * Read from ``GET /api/alarms`` with filter/pagination over
 * severity + acknowledged + muted state. Per-row ack + mute
 * actions identical to the per-chassis AlarmsTab. Includes a
 * jump-to-chassis link on every row.
 *
 * For the operator's mental model: the Alarms tab on a chassis
 * page is the *triage view* for one chassis; this page is the
 * *fleet overview*. Both lean on the same SP3 backend.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellOff, Check, ChevronRight, Filter, TimerReset } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime, formatLocal } from "../../../lib/datetime";
import { Badge } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { FilterBar } from "../../components/FilterBar";
import { SeverityPill } from "../../components/SeverityPill";
import { HelpButton } from "../../help/HelpPanel";
import { useToast } from "../../components/Toast";

type Alarm = {
  id: number;
  chassis_id: number;
  job_id: string | null;
  alarm_name: string;
  severity: string;
  resource: string;
  raised_at: string;
  parsed_data: Record<string, unknown> | null;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  muted_until: string | null;
  cleared_at: string | null;
};

type AlarmListResponse = {
  alarms: Alarm[];
  total: number;
};

type ChassisRow = {
  id: number;
  gigafy_device_name: string | null;
};

type ChassisListResponse = { chassis: ChassisRow[] };

const SEVERITY_OPTIONS = ["critical", "major", "minor", "warning", "info"];

function isMuted(a: Alarm): boolean {
  if (!a.muted_until) return false;
  return new Date(a.muted_until).getTime() > Date.now();
}

export function AllAlarms() {
  const qc = useQueryClient();
  const toast = useToast();
  const [severity, setSeverity] = useState<string | null>(null);
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [showMuted, setShowMuted] = useState(false);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const list = useQuery<AlarmListResponse>({
    queryKey: ["v2", "alarms", severity, showAcknowledged, showMuted, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (severity) params.set("severity", severity);
      // The endpoint takes ``acknowledged=true|false`` — interpret
      // our toggle as "include them too" rather than "show only".
      if (!showAcknowledged) params.set("acknowledged", "false");
      if (!showMuted) params.set("muted", "false");
      const res = await api.get<AlarmListResponse>(`/alarms?${params}`);
      return res.data;
    },
    refetchInterval: 30_000,
  });

  // Chassis name lookup so the rows show a friendly name rather than
  // raw IDs. Cheap query — fleet list rarely changes during a
  // session.
  const chassisQ = useQuery<ChassisListResponse>({
    queryKey: ["v2", "chassis-list-for-alarms"],
    queryFn: async () => {
      const res = await api.get<ChassisListResponse>("/chassis");
      return res.data;
    },
    staleTime: 60_000,
  });
  const chassisName = (id: number): string => {
    const row = chassisQ.data?.chassis.find((c) => c.id === id);
    return row?.gigafy_device_name ?? `chassis-${id}`;
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["v2", "alarms"] });
    qc.invalidateQueries({ queryKey: ["v2", "alarm-count"] });
    qc.invalidateQueries({ queryKey: ["v2", "alarm-count-by-chassis"] });
  };

  const ack = useMutation({
    mutationFn: async (id: number) => api.post(`/alarms/${id}/ack`, {}),
    onSuccess: () => {
      invalidate();
      toast.push("Alarm acknowledged", "success");
    },
  });
  const mute1h = useMutation({
    mutationFn: async (id: number) =>
      api.post(`/alarms/${id}/mute`, { duration_seconds: 3600 }),
    onSuccess: () => {
      invalidate();
      toast.push("Muted for 1h", "success");
    },
  });

  // Client-side substring filter over the fetched page (alarm name,
  // resource, severity, chassis name) — the shared FilterBar.
  const needle = filter.trim().toLowerCase();
  const allRows = list.data?.alarms ?? [];
  const rows = needle
    ? allRows.filter((a) =>
        [a.alarm_name, a.resource, a.severity, chassisName(a.chassis_id)].some(
          (v) => (v ?? "").toLowerCase().includes(needle),
        ),
      )
    : allRows;
  const total = list.data?.total ?? 0;

  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">All alarms</h1>
          <HelpButton topic="alarms" />
        </div>
        <p className="text-xs text-slate-500">
          Fleet-wide alarm list. Use the per-chassis Alarms tab when
          you're already focused on one device; this page is for
          finding what to triage first.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-900 shadow-border p-3 text-xs">
        <Filter className="h-3 w-3 text-slate-500" />
        <button
          type="button"
          onClick={() => {
            setSeverity(null);
            setPage(0);
          }}
          className={[
            "rounded border px-2 py-0.5",
            severity === null
              ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
              : "border-slate-700 text-slate-400 hover:bg-slate-800",
          ].join(" ")}
        >
          any severity
        </button>
        {SEVERITY_OPTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setSeverity(s === severity ? null : s);
              setPage(0);
            }}
            className={[
              "rounded border px-2 py-0.5",
              s === severity
                ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
                : "border-slate-700 text-slate-400 hover:bg-slate-800",
            ].join(" ")}
          >
            {s}
          </button>
        ))}
        <span className="ml-2 text-slate-600">|</span>
        <label className="inline-flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={showAcknowledged}
            onChange={(e) => {
              setShowAcknowledged(e.target.checked);
              setPage(0);
            }}
            className="h-3 w-3"
          />
          show acknowledged
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={showMuted}
            onChange={(e) => {
              setShowMuted(e.target.checked);
              setPage(0);
            }}
            className="h-3 w-3"
          />
          show muted
        </label>
        <FilterBar
          value={filter}
          onChange={setFilter}
          placeholder="Filter by alarm, resource, chassis…"
          className="w-64"
        />
        <span className="ml-auto tabular-nums text-slate-500">
          {total.toLocaleString()} rows · page {page + 1}
        </span>
        <button
          type="button"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        >
          ←
        </button>
        <button
          type="button"
          disabled={(page + 1) * PAGE_SIZE >= total}
          onClick={() => setPage((p) => p + 1)}
          className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        >
          →
        </button>
      </div>

      <div className="rounded-lg bg-slate-900 shadow-border p-3">
        {list.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-slate-500">
            No alarms match the current filter set.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Severity</th>
                <th className="px-2 py-1">Chassis</th>
                <th className="px-2 py-1">Alarm</th>
                <th className="px-2 py-1">Resource</th>
                <th className="px-2 py-1">Raised</th>
                <th className="px-2 py-1">State</th>
                <th className="px-2 py-1 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {rows.map((a) => {
                const muted = isMuted(a);
                const isDone = !!a.acknowledged_at || !!a.cleared_at;
                return (
                  <tr key={a.id} className={isDone ? "opacity-50" : ""}>
                    <td className="px-2 py-1.5">
                      <SeverityPill severity={a.severity} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Link
                        to={`/chassis/${a.chassis_id}#tab=alarms`}
                        className="inline-flex items-center gap-0.5 text-emerald-400 hover:text-emerald-300"
                      >
                        {chassisName(a.chassis_id)}
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    </td>
                    <td className="px-2 py-1.5">
                      <CopyableCell value={a.alarm_name} />
                    </td>
                    <td className="px-2 py-1.5">
                      <CopyableCell value={a.resource || null} />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                      <LocalTime iso={a.raised_at} />
                    </td>
                    <td className="px-2 py-1.5 text-[11px]">
                      {a.cleared_at ? (
                        <Badge tone="success">cleared</Badge>
                      ) : a.acknowledged_at ? (
                        <span className="text-slate-500">
                          ack by {a.acknowledged_by}
                        </span>
                      ) : muted ? (
                        <Badge tone="muted">muted</Badge>
                      ) : (
                        <Badge tone="warning">active</Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!a.acknowledged_at && (
                          <button
                            type="button"
                            onClick={() => ack.mutate(a.id)}
                            disabled={ack.isPending}
                            className="inline-flex items-center gap-1 rounded border border-emerald-800 bg-emerald-900/40 px-1.5 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
                          >
                            <Check className="h-3 w-3" />
                            ack
                          </button>
                        )}
                        {!muted && !a.acknowledged_at && (
                          <button
                            type="button"
                            onClick={() => mute1h.mutate(a.id)}
                            disabled={mute1h.isPending}
                            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-700 disabled:opacity-50"
                          >
                            <BellOff className="h-3 w-3" />
                            mute 1h
                          </button>
                        )}
                        {muted && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] text-amber-300"
                            title={`muted until ${formatLocal(a.muted_until)}`}
                          >
                            <TimerReset className="h-3 w-3" />
                            until
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
